import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from 'geojson';
import {
  Block,
  MAX_PLANT_SPACING,
  MAX_ROW_SPACING,
} from '../../services/entities/impl/blfApi/schemas/block';
import { UserRolesEnum } from '../../services/entities/impl/blfApi/schemas/user';
import { searchFields } from '../commonUtils';
import {
  MeasureAcresResult,
  validateAcres,
} from '../map/features/acreMeasure/hooks/useMeasureAcres';
import { acrePercentageDifferenceThreshold } from '../map/features/acreMeasure/MapBlockInfo';
import { MapDrawIDEnum } from '../onboarding/onboardingSlice';

export enum SendStatusEnum {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  FAILED = 'failed',
}

export interface SendStatus {
  status: SendStatusEnum;
  message: string;
}

export const initialSendStatus: SendStatus = {
  status: SendStatusEnum.IDLE,
  message: '',
};

/**
 * Validates if the input object is a valid geoJSON LineString or Polygon feature with valid sets of coordinates.
 *
 * @param {object} inputObj The input object to validate.
 * @param {'LineString' | 'Polygon'} geoFeatureType The type of geoJSON feature to validate.
 * @param {MapDrawIDEnum} mapDrawId The identifier for the feature.
 * @returns {boolean} Returns true if the input object is a valid geoJSON LineString or Polygon feature with valid sets of coordinates. Returns false otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isValidGeoFeature(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputObj: any,
  geoFeatureType: 'LineString' | 'Polygon',
  mapDrawId?: MapDrawIDEnum
): boolean {
  try {
    if (
      !inputObj ||
      typeof inputObj !== 'object' ||
      Object.keys(inputObj).length === 0
    ) {
      return false;
    }

    // If mapDrawId is provided, get the feature from the input object, otherwise use the input object directly
    let feature;
    if (mapDrawId) {
      feature = inputObj[mapDrawId];
    } else {
      feature = inputObj;
    }

    if (!feature) {
      return false;
    }

    if (!hasProperty(feature, 'type') || !hasProperty(feature, 'geometry')) {
      return false;
    }

    // If polygon is selected, allow MultiPolygon as well
    let geometryType = feature.geometry.type;
    if (geometryType === 'MultiPolygon') {
      geometryType = 'Polygon';
    }

    if (feature.type !== 'Feature' || geometryType !== geoFeatureType) {
      return false;
    }

    if (geoFeatureType === 'LineString') {
      return isValidLineString(feature.geometry);
    } else if (
      geoFeatureType === 'Polygon' ||
      geoFeatureType === 'MultiPolygon'
    ) {
      return isValidPolygon(feature.geometry);
    }

    return false;
  } catch (error) {
    console.error(error);
    return false;
  }
}

/**
 * Validates if the input geometry is a valid geoJSON LineString feature with valid sets of coordinates.
 *
 * @param {Geometry} geometry The geometry to validate.
 * @returns {boolean} Returns true if the input geometry is a valid geoJSON LineString feature with valid sets of coordinates. Returns false otherwise.
 */
export function isValidLineString(geometry: Geometry): boolean {
  if (!geometry || geometry.type !== 'LineString') {
    return false;
  }
  if (
    !Array.isArray(geometry.coordinates) ||
    geometry.coordinates.length !== 2
  ) {
    return false;
  }
  return geometry.coordinates.every(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (coordinate: any) =>
      Array.isArray(coordinate) &&
      coordinate.length === 2 &&
      typeof coordinate[0] === 'number' &&
      typeof coordinate[1] === 'number'
  );
}

/**
 * Validates if the input geometry is a valid geoJSON Polygon or MultiPolygon feature with valid sets of coordinates.
 *
 * @param {Geometry} geometry The geometry to validate.
 * @returns {boolean} Returns true if the input geometry is a valid geoJSON Polygon or MultiPolygon feature with valid sets of coordinates. Returns false otherwise.
 */
export function isValidPolygon(geometry: Geometry): boolean {
  if (
    !geometry ||
    (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')
  ) {
    console.log('Invalid geometry type');
    return false;
  }
  const polygons =
    geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  for (const polygon of polygons) {
    const coordinates = polygon[0];
    if (!Array.isArray(coordinates) || coordinates.length < 4) {
      console.log(
        'Invalid coordinates: Must be an array with at least 4 coordinates'
      );
      return false;
    }
    // The first and last coordinates must be the same to close the polygon.
    if (
      coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
      coordinates[0][1] !== coordinates[coordinates.length - 1][1]
    ) {
      console.log(
        'Invalid coordinates: First and last coordinates must be the same to close the polygon.'
      );
      return false;
    }
  }
  return true;
}

interface ValidateAndCreateFeatureCollectionResponse {
  featureCollection: FeatureCollection | null;
  message: string;
  error: boolean;
}

/**
 * Validates and creates a feature collection from an input object.
 * It searches through the input object and looks for features with the keys
 * MapDrawIDEnum.xNumberOrientation and MapDrawIDEnum.yRowOrientation.
 * It will then validate that these are valid geoJSON LineString features with
 * two valid sets of coordinates using isValidGeoFeature.
 * If they are both valid, it will return a geoJSON feature collection containing both features.
 * Otherwise, it will return null for the feature collection and an error message with the problem.
 *
 * @param {object} inputObj The input object to validate and create a feature collection from.
 * @returns {ValidateAndCreateFeatureCollectionResponse} Returns a feature collection if both xNumberOrientation and yRowOrientation
 * are valid geoJSON LineString features with two valid sets of coordinates. Returns null for the feature collection and an error message otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function valXYAndCreateFeatureCollection(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputObj: any
): ValidateAndCreateFeatureCollectionResponse {
  try {
    const xNumberOrientation = inputObj[MapDrawIDEnum.xNumberOrientation];
    const yRowOrientation = inputObj[MapDrawIDEnum.yRowOrientation];

    if (!xNumberOrientation || !yRowOrientation) {
      return {
        featureCollection: null,
        message:
          'Input object does not contain both xNumberOrientation and yRowOrientation features.',
        error: true,
      };
    }

    const isXNumberOrientationValid = isValidGeoFeature(
      inputObj,
      'LineString',
      MapDrawIDEnum.xNumberOrientation
    );
    const isYRowOrientationValid = isValidGeoFeature(
      inputObj,
      'LineString',
      MapDrawIDEnum.yRowOrientation
    );

    if (isXNumberOrientationValid && isYRowOrientationValid) {
      const newFeatureCollection: FeatureCollection = {
        type: 'FeatureCollection',
        features: [xNumberOrientation, yRowOrientation],
      };

      return {
        featureCollection: newFeatureCollection,
        message: 'Success',
        error: false,
      };
    } else {
      return {
        featureCollection: null,
        message:
          'xNumberOrientation and/or yRowOrientation are not valid LineString features.',
        error: true,
      };
    }
  } catch (error) {
    console.error(error);
    return {
      featureCollection: null,
      message:
        'An error occurred while validating and creating the feature collection.',
      error: true,
    };
  }
}

/**
 * Validates the geoPolygon and geoOrientation fields of a Block.
 * @param block The block to validate.
 * @returns {isValid: boolean, error: string} Returns an object containing a boolean indicating whether the block is valid and a string containing an error message if applicable.
 */
export function isValidBlock(block: Block | undefined): {
  isValid: boolean;
  error: string;
} {
  if (!block) {
    return { isValid: false, error: 'Block is undefined' };
  }
  const { geoPolygon, geoOrientation } = block;
  if (
    !geoPolygon ||
    !geoPolygon.features?.length ||
    !isValidPolygon(geoPolygon.features[0].geometry)
  ) {
    return { isValid: false, error: 'Invalid geoPolygon' };
  }
  if (!geoOrientation || !geoOrientation.features?.length) {
    return { isValid: false, error: 'geoOrientation is undefined' };
  }

  if (
    !isValidLineString(geoOrientation.features[0].geometry) ||
    !isValidLineString(geoOrientation.features[1].geometry)
  ) {
    return {
      isValid: false,
      error: 'Invalid LineString in geoOrientation',
    };
  }

  return { isValid: true, error: '' };
}

/**
 * Checks if the object has a specific property.
 *
 * @param obj - Object to check
 * @param prop - Property name to check
 */
function hasProperty(obj: object, prop: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * Validates a GeoJSON FeatureCollection object.
 *
 * @param {object} featureCollection - The GeoJSON FeatureCollection object to validate.
 * @returns {boolean} - Returns true if all features in the FeatureCollection are valid. Returns false otherwise.
 */
export function isValidFeatureCollection(featureCollection: unknown): boolean {
  try {
    if (
      !featureCollection ||
      typeof featureCollection !== 'object' ||
      !hasProperty(featureCollection as object, 'type') ||
      !hasProperty(featureCollection as object, 'features')
    ) {
      return false;
    }

    const typedFeatureCollection = featureCollection as {
      type: string;
      features: unknown[];
    };

    if (typedFeatureCollection.type !== 'FeatureCollection') {
      return false;
    }

    for (const feature of typedFeatureCollection.features) {
      // Assuming the MapDrawIDEnum is not needed here
      if (
        !isValidGeoFeature(feature, 'LineString') &&
        !isValidGeoFeature(feature, 'Polygon')
      ) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

/**
 * Cleans and formats a GeoJSON feature or feature collection into a feature collection.
 * This is useful for removing extra properties that are not needed from customers data during onboarding.
 * @param input - A GeoJSON feature or feature collection.
 * @returns A cleaned-up GeoJSON feature collection.
 */
export function cleanAndFormatGeoJSON(
  input:
    | Feature<Geometry, GeoJsonProperties>
    | FeatureCollection<Geometry, GeoJsonProperties>
): FeatureCollection<Geometry, GeoJsonProperties> {
  // Always return a FeatureCollection
  let cleanedFeatures: Feature<Geometry, GeoJsonProperties>[];

  if (input.type === 'Feature') {
    // Handle the Feature type

    // De-structure to obtain type and geometry
    const { type, geometry } = input;

    // Create cleaned feature
    const cleanedFeature: Feature<Geometry, GeoJsonProperties> = {
      type,
      geometry,
      properties: null, // Set properties to null
    };

    // Add bbox if it exists
    if ('bbox' in input) {
      cleanedFeature.bbox = input.bbox;
    }

    // Since the input is a single Feature, the FeatureCollection will have just one item
    cleanedFeatures = [cleanedFeature];
  } else if (input.type === 'FeatureCollection') {
    // Handle the FeatureCollection type

    // De-structure to obtain features
    const { features } = input;

    // Clean each feature
    cleanedFeatures = features
      .map(
        (feature) =>
          cleanAndFormatGeoJSON(feature) as FeatureCollection<
            Geometry,
            GeoJsonProperties
          >
      )
      .flatMap((fc) => fc.features);

    // Note: We use flatMap above to flatten the array of feature collections into a single array of features
  } else {
    throw new Error(
      'Invalid GeoJSON type. Only "Feature" and "FeatureCollection" are supported.'
    );
  }

  // Create a cleaned FeatureCollection
  const cleanedFeatureCollection: FeatureCollection<
    Geometry,
    GeoJsonProperties
  > = {
    type: 'FeatureCollection',
    features: cleanedFeatures,
  };

  // Add bbox if it exists and the input was a FeatureCollection
  if ('bbox' in input && input.type === 'FeatureCollection') {
    cleanedFeatureCollection.bbox = input.bbox;
  }

  return cleanedFeatureCollection;
}

/**
 * Returns the input string if it is not empty, or a single space or non-breaking space if it is empty.
 * This function is intended for use as a height placeholder for text.
 *
 * @param str - The input string
 * @param useNonBreakingSpace - A boolean indicating whether to use a non-breaking space (defaults to false)
 * @returns The input string, or a space or non-breaking space if the input string is empty
 */
export function formatHelperText(
  str: string,
  useNonBreakingSpace = false
): string {
  return str.trim() === '' ? (useNonBreakingSpace ? '\u00A0' : ' ') : str;
}

// Create a Set from UserRolesEnum values for faster lookup
const userRolesSet: Set<string> = new Set(Object.values(UserRolesEnum));

/**
 * Function to check if the given text matches any role in the UserRolesEnum.
 *
 * @param {string} text - The text to check.
 * @return {boolean} - True if the text matches any role, false otherwise.
 */
export const isUserRole = (text: string): boolean => {
  return userRolesSet.has(text.toLowerCase());
};

type AdditionalBlockValidationResults = {
  errors: string[];
  warnings: string[];
  acresMismatch: boolean;
  acresResult: MeasureAcresResult;
};

/**
 * Validates optional fields of a block and returns errors and warnings.
 * @param block - The block to validate.
 * @returns An object containing errors and warnings.
 */
export function validateOptionalFieldsBlock(
  block: Block
): AdditionalBlockValidationResults {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check plantSpacing
  if (block.plantSpacing === null || block.plantSpacing === undefined) {
    warnings.push('Plant spacing is missing');
  } else if (block.plantSpacing <= 0) {
    errors.push('Plant spacing must be a positive number');
  } else if (block.plantSpacing > MAX_PLANT_SPACING) {
    warnings.push(`Plant spacing is greater than ${MAX_PLANT_SPACING}`);
  }

  // Check rowSpacing
  if (block.rowSpacing === null || block.rowSpacing === undefined) {
    warnings.push('Row spacing is missing');
  } else if (block.rowSpacing <= 0) {
    errors.push('Row spacing must be a positive number.');
  } else if (block.rowSpacing > MAX_ROW_SPACING) {
    warnings.push(`Row spacing is greater than ${MAX_ROW_SPACING}`);
  }

  // Check geoPolygon
  if (block.geoPolygon === null || block.geoPolygon === undefined) {
    warnings.push('Block boundary is missing');
  } else if (
    block.geoPolygon.features?.length &&
    !isValidPolygon(block.geoPolygon.features[0].geometry)
  ) {
    errors.push('Invalid block boundary');
  }

  // Check geoOrientation
  if (block.geoOrientation === null || block.geoOrientation === undefined) {
    warnings.push('Block orientation is missing');
  } else if (
    block.geoOrientation.features?.length !== 2 &&
    (!isValidGeoFeature(
      block.geoOrientation.features[0].geometry,
      'LineString',
      MapDrawIDEnum.xNumberOrientation
    ) ||
      !isValidGeoFeature(
        block.geoOrientation.features[1].geometry,
        'LineString',
        MapDrawIDEnum.yRowOrientation
      ))
  ) {
    errors.push('Invalid orientation vectors');
  }

  // Check Acres
  let acresMismatch = false;
  const acresResult = validateAcres(undefined, block, false);
  if (acresResult.calculatedAcres && acresResult.userInputAcres) {
    const percentageDifference =
      (Math.abs(acresResult.userInputAcres - acresResult.calculatedAcres) /
        acresResult.userInputAcres) *
      100;

    if (percentageDifference > acrePercentageDifferenceThreshold) {
      acresMismatch = true;
      warnings.push(
        `Input acres deviates by more than ${acrePercentageDifferenceThreshold}% from calculated acres`
      );
    }
  }

  return { errors, warnings, acresMismatch, acresResult };
}

/**
 * Searches for a given search value within the provided errors and warnings arrays.
 *
 * @param searchVal - The search value to look for in the errors and warnings arrays.
 * @param errors - Optional array of error strings.
 * @param warnings - Optional array of warning strings.
 *
 * @returns - Boolean indicating whether the search value was found in either the errors or warnings arrays.
 */
export function searchErrorsAndWarnings(
  searchVal: string,
  errors?: string[],
  warnings?: string[]
): boolean {
  // Normalize the search value to lower case for case-insensitive search
  const normalizedSearchVal = searchVal.toLowerCase();

  // Search in errors array if it's provided
  if (errors) {
    if (
      errors.some((error) => error.toLowerCase().includes(normalizedSearchVal))
    ) {
      return true;
    }
  }

  // Search in warnings array if it's provided
  if (warnings) {
    if (
      warnings.some((warning) =>
        warning.toLowerCase().includes(normalizedSearchVal)
      )
    ) {
      return true;
    }
  }

  // Return false if search value not found in either arrays
  return false;
}

interface ShowParams<T> {
  searchVal: string | null;
  searchObject: T;
  searchableFields: (keyof T)[];
  showInvalidOnly: boolean;
  errors?: string[];
  warnings?: string[];
  additionalSearchItems?: string[];
}

/**
 * Determines whether a given item should be shown based on multiple conditions.
 *
 * @param params - An object containing:
 *   - searchVal: The search value to look for in the searchable fields and errors/warnings arrays.
 *   - searchObject: The object with searchable fields.
 *   - searchableFields: An array of fields in the object that should be searched.
 *   - showInvalidOnly: Flag to indicate whether only invalid items should be shown.
 *   - errors: Optional array of error strings.
 *   - warnings: Optional array of warning strings.
 *   - additionalSearchItems: Optional array of additional items to be searched.
 *
 * @returns - Boolean indicating whether the item should be shown.
 */
export function shouldShowItem<T>(params: ShowParams<T>): boolean {
  const {
    searchVal,
    searchObject,
    searchableFields,
    showInvalidOnly,
    errors,
    warnings,
    additionalSearchItems,
  } = params;

  let matchedSearchField = false;
  let matchedErrorOrWarning = false;
  let matchedAdditionalItems = false;

  // Check if the search value matches any field in the object
  if (searchVal) {
    matchedSearchField = searchFields(
      searchVal,
      searchObject,
      searchableFields
    );
    matchedErrorOrWarning = searchErrorsAndWarnings(
      searchVal,
      errors,
      warnings
    );

    // Check if searchVal matches any item in the additionalSearchItems array
    if (additionalSearchItems) {
      matchedAdditionalItems = additionalSearchItems.some((item) =>
        item.toLowerCase().includes(searchVal.toLowerCase())
      );
    }
  }

  // If 'showInvalidOnly' is true but there are no errors or warnings, don't show this item
  if (
    showInvalidOnly &&
    (!errors || errors.length === 0) &&
    (!warnings || warnings.length === 0)
  ) {
    return false;
  }

  // If there is a search value but it doesn't match anything, don't show this item
  if (
    searchVal &&
    !matchedSearchField &&
    !matchedErrorOrWarning &&
    !matchedAdditionalItems
  ) {
    return false;
  }

  return true;
}

/**
 * Translates the error message for form fields.
 * This function checks if the provided error message is a string and uses the provided translation function to translate it.
 * It's designed to be used with form validation error messages to ensure they are correctly translated in the UI.
 *
 * @param {any} error - The error message to be translated. Can be of any type, but translation will only occur for strings.
 * @param {(key: string, options?: { ns: string }) => string} translateFn - The translation function, from useTranslation hook.
 * @returns {string} The translated error message if it's a string, or an empty string for other types or if translation is not needed.
 */
export const getErrorMessage = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  translateFn: (key: string, options?: { ns: string }) => string
): string => {
  if (typeof error === 'string') {
    return translateFn(error, { ns: 'common' }); // Translate only if error is a string
  }
  return ''; // Default fallback for non-string errors
};
